import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

// React-leaflet needs its children to be direct descendants of MapContainer so
// each layer registers with the underlying map instance. `<div>` breaks that;
// this tiny wrapper keeps the JSX tidy while staying transparent.
const FragmentWithKey = Fragment;

delete L.Icon.Default.prototype._getIconUrl;

const KIND_ICON = {
    // Fallback icon per layer (used when category doesn't match anything specific).
    eat_drink: "🍽",
    shop:      "🛍",
    services:  "🛠",
    beauty:    "💆",
    produce:   "🌾",
    venue:     "🎭",
    // Legacy raw-kind icons — kept so residual pins still render
    business:  "🏬",
    artist:    "🎨",
    maker:     "🛠",
    event:     "🎪",
    gig:       "🎵",
};

// Fine-grained category → emoji. Keyword matched (free-text tolerant), first
// match wins. Ordered from most-specific → least-specific inside each layer so
// e.g. "gallery shop" hits shop, but "gallery" alone hits venue.
const CATEGORY_ICON_RULES = [
    // Farms & producers first — "farm shop" / "coffee roaster" must not hit shop / café
    ["farm shop",                               "🧺"],
    ["pick-your-own",                           "🍓"],
    ["dairy|cheese",                            "🧀"],
    ["eggs|poultry",                            "🥚"],
    ["beekeep|honey",                           "🍯"],
    ["orchard|cider",                           "🍎"],
    ["market garden|veg grower",                "🥕"],
    ["mill|flour",                              "🌾"],
    ["distillery",                              "🥃"],
    ["vineyard|winery",                         "🍇"],
    ["roaster",                                 "☕"],
    ["smokehouse|fishery",                      "🐟"],
    ["chocolatier",                             "🍫"],
    ["preserves|sauces",                        "🫙"],
    ["forager|wild food",                       "🍄"],
    ["farm|livestock",                          "🐄"],
    // Health & beauty — specific first so the generic salon icon doesn't claim them
    ["nail",                                    "💅"],
    ["barber",                                  "💈"],
    ["tattoo|piercing",                         "🖋"],
    ["massage|wellness",                        "💆"],
    ["yoga|pilates",                            "🧘"],
    ["pharmacy|chemist",                        "💊"],
    ["optician",                                "👓"],
    ["stay (hotel|hotel|b&b|guest house",       "🛏"],
    // Lessons & mobile services — checked first so "music"/"pet" rules don't claim them
    ["driving instructor|driving school",      "🚗"],
    ["music lesson",                            "🎼"],
    ["tutor|lessons",                           "📚"],
    ["personal trainer",                        "🏋"],
    ["dog walker|pet sitter",                   "🐕"],
    // Eat & Drink (specific first)
    ["bakery|bakehouse|bake|patisserie|pastr", "🥐"],
    ["pub|tavern|beer|brewery",                "🍺"],
    ["wine|cocktail",                           "🍷"],
    ["distillery|whisky|whiskey|spirits",       "🥃"],
    ["coffee|café|cafe",                        "☕"],
    ["tea room|tea shop|teahouse|teahouses",    "🍵"],
    ["ice cream|gelato",                        "🍦"],
    ["pizza|pizzeria",                          "🍕"],
    ["grocer|deli|market stall|farm shop",      "🥬"],
    ["restaurant|bistro|diner|eatery|kitchen|canteen|food", "🍽"],
    ["bar",                                     "🍺"],

    // Shops
    ["book",                                    "📚"],
    ["record|vinyl",                            "💿"],
    ["florist|flower",                          "💐"],
    ["gift",                                    "🎁"],
    ["vintage",                                 "🕰"],
    ["boutique|clothing|fashion",               "👗"],
    ["retail|shop|store",                       "🛍"],

    // Venues & Spaces (evaluated before generic shop/service catches)
    ["theatre|theater|playhouse|opera",         "🎭"],
    ["cinema",                                  "🎬"],
    ["music venue|gig venue|live music",        "🎤"],
    ["gallery|exhibition",                      "🖼"],
    ["museum|heritage",                         "🏛"],
    ["community hall|community centre|community space", "🏘"],
    ["event venue|event space",                 "🎉"],
    ["hall|auditorium|arena|stadium",           "🏟"],

    // Services & Wellness
    ["salon|barber|hair|nail",                  "💇"],
    ["spa|beauty|wellness|massage",             "🧖"],
    ["fitness|gym|pilates",                     "🧘"],
    ["yoga",                                    "🧘"],
    ["hotel|b&b|bnb|stay|guesthouse|hostel",    "🛏"],

    // Makers & Artists
    ["ceramic|pottery|clay|stoneware",          "🏺"],
    ["jewel|jewellery|jewelry",                 "💍"],
    ["textile|weave|fibre|knit|yarn",           "🧵"],
    ["leather",                                 "👜"],
    ["wood|carpent|joiner",                     "🪵"],
    ["candle|soap",                             "🕯"],
    ["print|screen print|risograph|linocut",    "🖨"],
    ["illustration|illustrator",                "🖌"],
    ["sculpt",                                  "🗿"],
    ["photograph|photo",                        "📷"],
    ["paint",                                   "🎨"],
    ["coworking|co-working|studio space|workshop space|makerspace|maker space", "🖥"],
];

export function iconForListingCategory(l) {
    if (l.marker_icon) return l.marker_icon;
    const c = (l.category || "").toLowerCase();
    if (/\bspa\b/.test(c)) return "💆";
    for (const [pattern, emoji] of CATEGORY_ICON_RULES) {
        const alts = pattern.split("|");
        if (alts.some((a) => c.includes(a))) return emoji;
    }
    const layer = resolveLayer(l);
    return KIND_ICON[layer] || KIND_ICON[l.kind] || "📍";
}

// Per-layer cluster colour — matches the layer chip on Home so the map "reads"
// as its layer at a glance. `business` split into 3 intent-driven sub-layers
// (2026-09-13 — Eat & Drink / Shops / Services & Wellness).
const LAYER_COLOR = {
    eat_drink: "hsl(27 87% 55%)",
    shop:      "hsl(131 22% 29%)",
    services:  "hsl(180 45% 40%)",
    beauty:    "hsl(330 55% 52%)",
    produce:   "hsl(85 40% 34%)",
    creator:   "hsl(20 78% 60%)",
    // legacy keys kept so any residual per-kind markers still resolve
    business:  "hsl(131 22% 29%)",
    artist:    "hsl(20 78% 60%)",
    maker:     "hsl(20 78% 60%)",
    event:     "hsl(220 55% 45%)",
    photo:     "hsl(290 45% 55%)",
    gig:       "hsl(340 82% 55%)",
};

// Business category → sub-layer. Mirrors Home.jsx — keyword matching so
// free-text categories (e.g. "Bakery and coffee shop") still land correctly.
import { resolvePrimaryLayer as resolveLayer } from "@/lib/taxonomy";
import { thumb } from "@/lib/img";

// Lift a pin above its neighbours. Done on the DOM node (not by swapping the icon)
// so a fanned-out cluster stays open when you pick one of its pins.
const RAISED_Z = 1000;
const raise = (m, on) => {
    if (!m) return;
    m.setZIndexOffset(on ? RAISED_Z : 0);
    m.getElement()?.classList.toggle("marker-raised", on);
};

function RaisableMarker({ lat, lng, icon, selected, onClick }) {
    const ref = useRef(null);
    // Stable array — a new one makes Leaflet think the pin moved and re-cluster it.
    const position = useMemo(() => [lat, lng], [lat, lng]);
    useEffect(() => { raise(ref.current, selected); }, [selected]);
    return (
        <Marker
            ref={ref}
            position={position}
            icon={icon}
            eventHandlers={{
                click: onClick,
                add: (e) => selected && raise(e.target, true),
                mouseover: (e) => raise(e.target, true),
                mouseout: (e) => !selected && raise(e.target, false),
            }}
        />
    );
}

// Icons are cached so re-renders never hand Leaflet a "new" icon.
const iconCache = new Map();
const cached = (key, make) => {
    if (!iconCache.has(key)) iconCache.set(key, make());
    return iconCache.get(key);
};

function makeDivIcon(layer, symbol, tonight = false, founding = false, approx = false) {
    return cached(`pin|${layer}|${symbol}|${tonight}|${founding}|${approx}`, () => {
        const badge = (tonight ? '<div class="marker-tonight-dot" title="Something on tonight"></div>' : '')
            + (founding ? '<div class="marker-founding-ribbon" title="Founding pin"></div>' : '')
            + (approx ? '<div class="marker-approx-badge" title="Approximate location">≈</div>' : '');
        return L.divIcon({
            className: "custom-marker",
            html: `<div class="custom-marker-pin marker-${layer}${approx ? " marker-approx" : ""}">${badge}<span style="font-size:16px">${symbol || KIND_ICON[layer] || "📍"}</span></div>`,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
        });
    });
}

const GIG_KIND_ICON = {
    music: "🎵", quiz: "🎯", art: "🖼", performance: "🎭", comedy: "🎤", other: "✨",
};

function makeGigIcon(kind, cancelled = false) {
    const symbol = GIG_KIND_ICON[kind] || "🎵";
    return cached(`gig|${kind}|${cancelled}`, () => L.divIcon({
        className: "custom-marker",
        html: `<div class="gig-marker${cancelled ? " gig-marker-cancelled" : ""}"><span style="font-size:16px">${symbol}</span></div>`,
        iconSize: [42, 42],
        iconAnchor: [21, 42],
    }));
}

function makePhotoIcon(imageUrl) {
    return cached(`photo|${imageUrl}`, () => L.divIcon({
        className: "custom-marker",
        html: `<div class="photo-marker" style="background-image:url('${thumb(imageUrl, 200)}')"></div>`,
        iconSize: [56, 56],
        iconAnchor: [28, 56],
    }));
}

function makeOriginIcon(label = "Home", color = "hsl(131 22% 29%)") {
    return L.divIcon({
        className: "custom-marker",
        html: `<div class="origin-marker" style="background:${color}"><span>${label === "You are here" ? "📍" : label === "Scouting" ? "🔎" : "🏠"}</span></div>`,
        iconSize: [44, 44],
        iconAnchor: [22, 22],
    });
}

// A cluster bubble that adopts the layer's colour. Size scales with count.
const clusterIconFns = new Map();
function makeClusterIcon(layer) {
    if (clusterIconFns.has(layer)) return clusterIconFns.get(layer);
    const color = LAYER_COLOR[layer] || "hsl(131 22% 29%)";
    const fn = (cluster) => {
        const count = cluster.getChildCount();
        const size = count < 10 ? 40 : count < 50 ? 48 : count < 200 ? 58 : 68;
        return L.divIcon({
            html: `<div class="cluster-bubble cluster-${layer}" style="--cluster-color:${color};width:${size}px;height:${size}px"><span>${count}</span></div>`,
            className: "cluster-marker",
            iconSize: L.point(size, size, true),
        });
    };
    clusterIconFns.set(layer, fn);
    return fn;
}

function MapClickCatcher({ onMapClick, scoutMode }) {
    useMapEvents({
        click(e) {
            // Always forward map clicks — Home.jsx uses them for BOTH "dismiss open card"
            // (when a pin/photo/gig is selected) AND "drop scout pin" (scout mode).
            if (onMapClick) onMapClick({ lat: e.latlng.lat, lng: e.latlng.lng, scoutMode: !!scoutMode });
        },
    });
    const map = useMap();
    useEffect(() => {
        const container = map.getContainer();
        container.style.cursor = scoutMode ? "crosshair" : "";
        return () => { container.style.cursor = ""; };
    }, [scoutMode, map]);
    return null;
}

function FitBounds({ points, origin, disabled, restored }) {
    const map = useMap();
    // Keep `disabled` in a ref so flipping it (fly complete) does NOT retrigger the effect
    // and snap the map back to the global bounds.
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;
    // Returning to a saved view: hold it until the origin actually changes.
    const holdRef = useRef(restored ? `${origin?.lat},${origin?.lng}` : null);
    useEffect(() => {
        if (disabledRef.current) return;
        if (holdRef.current !== null) {
            if (holdRef.current === `${origin?.lat},${origin?.lng}`) return;
            holdRef.current = null;
        }
        const pts = [...points];
        if (origin) pts.push(origin);
        if (!pts.length) return;
        const bounds = L.latLngBounds(pts.map((p) => [p.lat, p.lng]));
        map.fitBounds(bounds, { padding: [80, 80], maxZoom: 14 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [points.length, origin?.lat, origin?.lng]);
    return null;
}

function FitTo({ target }) {
    const map = useMap();
    useEffect(() => {
        if (!target?.points?.length) return;
        if (target.points.length === 1) map.flyTo([target.points[0].lat, target.points[0].lng], 16, { duration: 0.9 });
        else map.flyToBounds(L.latLngBounds(target.points.map((p) => [p.lat, p.lng])), { padding: [70, 70], maxZoom: 16, duration: 0.9 });
    }, [target, map]);
    return null;
}

const VIEW_KEY = "fromhere_map_view_v1";
export const readSavedView = () => {
    try { return JSON.parse(sessionStorage.getItem(VIEW_KEY) || "null"); } catch { return null; }
};

function RememberView() {
    useMapEvents({
        moveend: (e) => {
            const c = e.target.getCenter();
            try { sessionStorage.setItem(VIEW_KEY, JSON.stringify({ lat: c.lat, lng: c.lng, zoom: e.target.getZoom() })); } catch {}
        },
    });
    return null;
}

function FlyTo({ target, onDone }) {
    const map = useMap();
    useEffect(() => {
        if (!target) return;
        map.flyTo([target.lat, target.lng], target.zoom || 14, { duration: 0.9 });
        const handler = () => onDone?.();
        map.once("moveend", handler);
        return () => { map.off("moveend", handler); };
    }, [target, map, onDone]);
    return null;
}

// Shared cluster options — one instance per layer keeps the counts split.
const clusterOpts = {
    chunkedLoading: true,
    showCoverageOnHover: false,
    spiderfyOnMaxZoom: true,
    // Shrink the grouping radius as you zoom in; pins sharing a spot stay grouped
    // and fan out into a circle when tapped.
    maxClusterRadius: (zoom) => (zoom >= 17 ? 14 : zoom >= 15 ? 35 : 55),
    spiderfyDistanceMultiplier: 1.6,
};

export default function MapView({ listings = [], photos = [], gigs = [], origin = null, originLabel = "Home", radiusKm = 0, onSelect, onSelectPhoto, onSelectGig, onMapClick, scoutMode = false, flyToTarget = null, onFlyToDone, tonightPinIds = null, showOriginMarker = true, selectedKey = null, fitTarget = null }) {
    const mapRef = useRef(null);
    const [savedView] = useState(readSavedView);
    const icons = useMemo(() => ({
        // New sub-layer icons (route based on category via resolveLayer)
        eat_drink: makeDivIcon("eat_drink"),
        shop:      makeDivIcon("shop"),
        services:  makeDivIcon("services"),
        beauty:    makeDivIcon("beauty"),
        produce:   makeDivIcon("produce"),
        venue:     makeDivIcon("venue"),
        // Legacy raw-kind icons kept as fallbacks
        business:  makeDivIcon("business"),
        artist:    makeDivIcon("artist"),
        maker:     makeDivIcon("maker"),
        event:     makeDivIcon("event"),
    }), []);
    const originIcon = useMemo(() => {
        const color = originLabel === "You are here" ? "hsl(220 55% 45%)"
                    : originLabel === "Scouting"      ? "hsl(290 45% 55%)"
                    : "hsl(131 22% 29%)";
        return makeOriginIcon(originLabel, color);
    }, [originLabel]);

    const all = [...listings, ...photos, ...gigs];
    const center = savedView ? [savedView.lat, savedView.lng] : origin ? [origin.lat, origin.lng] : (all[0] ? [all[0].lat, all[0].lng] : [55.8642, -4.2518]);
    const circleColor = originLabel === "You are here" ? "hsl(220 55% 45%)"
                      : originLabel === "Scouting"      ? "hsl(290 45% 55%)"
                      : "hsl(131 22% 29%)";

    // Split listings into per-LAYER buckets so each layer clusters on its own.
    // Business kind now splits three ways (Eat & Drink / Shops / Services) via
    // category; every listing routes through `resolveLayer` to land in the right bucket.
    const byLayer = useMemo(() => {
        const out = { produce: [], eat_drink: [], shop: [], services: [], beauty: [], venue: [], creator: [], event: [] };
        for (const l of listings) {
            const layer = resolveLayer(l);
            if (out[layer]) out[layer].push(l);
        }
        return out;
    }, [listings]);

    return (
        <div className="h-full w-full relative" data-testid="map-container">
            <MapContainer
                center={center}
                zoom={savedView?.zoom || 13}
                scrollWheelZoom={true}
                zoomControl={false}
                style={{ height: "100%", width: "100%" }}
                ref={mapRef}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    keepBuffer={4}
                    crossOrigin="anonymous"
                    updateWhenZooming={false}
                />
                {origin && radiusKm > 0 && (
                    <Circle
                        center={[origin.lat, origin.lng]}
                        radius={radiusKm * 1000}
                        pathOptions={{ color: circleColor, weight: 2, fillColor: circleColor, fillOpacity: 0.06 }}
                    />
                )}
                {origin && showOriginMarker && (
                    <Marker position={[origin.lat, origin.lng]} icon={originIcon} interactive={false} />
                )}

                {["produce", "eat_drink", "shop", "services", "beauty", "venue", "creator", "event"].map((layer) => {
                    const rows = byLayer[layer];
                    if (!rows.length) return null;
                    const exact = rows;
                    // Build a per-pin icon at render time — colour by layer, emoji by category.
                    const iconForListing = (l) => makeDivIcon(resolveLayer(l), iconForListingCategory(l), !!(tonightPinIds && tonightPinIds.has && tonightPinIds.has(l.listing_id)), !!l.founding, !!l.is_fuzzy);
                    return (
                        <FragmentWithKey key={`layer-${layer}`}>
                            {exact.length > 0 && (
                                <MarkerClusterGroup
                                    {...clusterOpts}
                                    iconCreateFunction={makeClusterIcon(layer)}
                                >
                                    {exact.map((l) => (
                                        <RaisableMarker
                                            key={l.listing_id}
                                            lat={l.lat} lng={l.lng}
                                            icon={iconForListing(l)}
                                            selected={selectedKey === `listing:${l.listing_id}`}
                                            onClick={() => onSelect?.(l)}
                                        />
                                    ))}
                                </MarkerClusterGroup>
                            )}
                                                    </FragmentWithKey>
                    );
                })}

                {photos.length > 0 && (
                    <MarkerClusterGroup {...clusterOpts} iconCreateFunction={makeClusterIcon("photo")}>
                        {photos.map((p) => (
                            <RaisableMarker
                                key={p.photo_id}
                                lat={p.lat} lng={p.lng}
                                icon={makePhotoIcon(p.image_url)}
                                selected={selectedKey === `photo:${p.photo_id}`}
                                onClick={() => onSelectPhoto?.(p)}
                            />
                        ))}
                    </MarkerClusterGroup>
                )}

                {gigs.length > 0 && (
                    <MarkerClusterGroup {...clusterOpts} iconCreateFunction={makeClusterIcon("gig")}>
                        {gigs.map((g) => (
                            <RaisableMarker
                                key={g.gig_id}
                                lat={g.lat} lng={g.lng}
                                icon={makeGigIcon(g.kind, g.cancelled)}
                                selected={selectedKey === `gig:${g.gig_id}`}
                                onClick={() => onSelectGig?.(g)}
                            />
                        ))}
                    </MarkerClusterGroup>
                )}

                <FitBounds points={all} origin={origin} disabled={!!flyToTarget} restored={!!savedView} />
                <FitTo target={fitTarget} />
                <RememberView />
                <FlyTo target={flyToTarget} onDone={onFlyToDone} />
                <MapClickCatcher onMapClick={onMapClick} scoutMode={scoutMode} />
            </MapContainer>
        </div>
    );
}
